import { COMPONENT_TYPE, CountryCode, INPUT_COMPONENT_TYPE } from '@abiglobalhealth/go-types'
import { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { setFulfillmentPatientInfo, updateUser } from '@/api/user'
import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { PhoneNumber } from '@/components/PhoneNumber'
import { useConsultation } from '@/hooks/useConsultation'
import { useFormErrors } from '@/hooks/useFormErrors'
import { useLoading } from '@/hooks/useLoading'
import { useUser } from '@/hooks/useUser'
import { requiredString } from '@/utils/zod'

const zodSchema = z.object({
  phone: requiredString,
  address: requiredString
})

type SignatureRXFormData = z.infer<typeof zodSchema>

export const SignatureRXForm = () => {
  const { t } = useTranslation()
  const { showLoading, hideLoading } = useLoading()
  const { user, updateUser: updateLocalUser } = useUser()
  const { updateConsultation } = useConsultation()
  const { addFormError } = useFormErrors()

  const handleConfirm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const formData = new FormData(e.currentTarget)
    const data = {
      address: formData.get('address'),
      phone: formData.get('phone')
    }

    try {
      zodSchema.parse(data)
    } catch (err) {
      if (err instanceof z.ZodError) {
        for (const error of err.issues) {
          addFormError(error.path.join('.'), error.message)
        }
      } else {
        console.error(err)
      }

      return
    }

    showLoading()

    const updatedUser = await updateUser({
      user: {
        ...user?.data,
        ...(data as SignatureRXFormData)
      }
    })
    updateLocalUser(updatedUser)

    const { consultation: updatedConsultation } = await setFulfillmentPatientInfo({
      uniqueId: user?.data?.uniqueId!,
      ...(data as SignatureRXFormData)
    })
    updateConsultation(updatedConsultation)

    hideLoading()
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <p>{t('prescription.patient.address')}</p>

      <form id="user-address-form" onSubmit={handleConfirm}>
        <div className="flex flex-col py-4">
          <PhoneNumber
            id="phone"
            required
            tabIndex={0}
            defaultCountry={user?.data.country?.code?.toUpperCase()?.split('-')[0] as CountryCode}
            value={user?.data?.phone}
            textKey="inputs.phone.label"
          />

          <Input
            componentType={COMPONENT_TYPE.INPUT}
            id="address"
            tabIndex={1}
            type={INPUT_COMPONENT_TYPE.TEXTAREA}
            textKey={'delivery.address.address'}
            placeholder={'inputs.address.placeholder'}
            value={user?.data?.address}
          />
        </div>

        <Button>{t('button.confirm')}</Button>
      </form>
    </div>
  )
}
